import React, { useCallback, useMemo } from 'react';
import { sortFundNames } from '../../../../utilities/customSorting';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { createSectionByIdSelector } from '../../../../redux/pages/selectors';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useAllFundNotes } from '../../../../hooks/useFundNotes';
import { Note } from '../../../../sharedInterfaces/notes';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { RSOverviewPageModel, RSPageFund } from '../../../../api/api.types';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';
import {
  ReportsListByFund,
  useOverviewQueries,
} from '../../../../api/rs-hg/risk/hooks/queries';
import {
  addSupplementaryRulesColumn,
  getOverviewColumnsFields,
} from './overviewColumnFields';
import OverviewTableV2 from './OverviewTableV2';
import OverviewSecondLevelGraphs from './OverviewSecondLevelGraphs.component';
import { mapStatusToColor } from '../../../../utilities/colorUtilities';
import { specifyStatus } from '../../../../utilities/generalMappings';
import { Status } from '../../../../types/redux/data/dataTypes';
import {
  getOverviewColumnsV2,
  overviewInitialColumnVisibility,
} from './overviewColumnsV2';

type FundType = 'var' | 'commitment' | 'both';

/** Fund types that should show both Commitment and VaR on the overview. */
const SHOW_BOTH_METRICS_FUND_TYPES = new Set([
  'AIF',
  'AIF_PE',
  'QIAIF',
  'RAIF',
  'RIAIF',
  'TRUST',
]);

export interface GeneralOverviewTableData {
  internalFundName: string;
  fundName: string;
  isDemoFund: boolean;
  reports: string[];
  subFundRiskReports: string[];
  positionDate: string;
  calculationDate: string;
  currency: string;
  netAssetValue: number;
  commitmentValue: number;
  commitmentStatus: Status;
  valueAtRiskStatus: Status;
  valueAtRiskValue: number;
  varFund: string;
  varDay: string;
  ucitsLaw: Status;
  prospectusStatus: Status;
  countryExposure: any;
  positionExposure: any;
  index: number;
  derogationMessage?: string;
  timeSeriesData: any | null;
  timeSeriesStatus: any | null;
  notes: boolean;
  leverage: Status;
  fundType: FundType;
  act40Law?: Status;
  riaifLaw?: Status;
  qiaifLaw?: Status;
  nursLaw?: Status;
  fivgLaw?: Status;
  partiiLaw?: Status;
  cssfRiskSpreading?: Status;
  supplementaryRules?: Status;
  totalCommitmentPercent?: number;
  fundVar?: number;
  totalGrossExposurePercent?: number;
  market?: Status;
  counterparty?: Status;
  credit?: Status;
  liquidity?: Status;
}

function createGeneralTableData(
  inputData: any[],
  pageFunds: RSPageFund[],
  reportsList: ReportsListByFund,
  subFundRiskReportsList: any,
  clientName: string | null,
): GeneralOverviewTableData[] {
  const finalData: GeneralOverviewTableData[] = [];
  const pageFundByName = new Map(
    pageFunds.map((pageFund) => [pageFund.fund_name, pageFund]),
  );

  if (inputData.length && reportsList) {
    const reports = reportsList;
    const subFundRiskReports = subFundRiskReportsList ?? null;

    inputData.forEach((fund: any, index: number) => {
      let fundType: FundType =
        fund.monitor_data.var_fund === 'yes' ? 'var' : 'commitment';

      if (clientName?.includes('pure')) {
        fundType = 'both';
      }

      const pageFund = pageFundByName.get(fund.fund_name);
      if (
        pageFund?.fund_type &&
        SHOW_BOTH_METRICS_FUND_TYPES.has(pageFund.fund_type)
      ) {
        fundType = 'both';
      }

      const toAdd: GeneralOverviewTableData = {
        index: index + 1,
        internalFundName: fund.fund_name,
        fundName: fund.fund_name_full,
        reports: reports[fund.fund_name],
        subFundRiskReports: subFundRiskReports
          ? subFundRiskReports[fund.fund_name]
          : [],
        positionDate: fund.monitor_data.selected_position_date,
        calculationDate: fund.monitor_data.latest_calculation_timestamp,
        currency: fund.monitor_data.base_currency,
        netAssetValue: fund.monitor_data.nav,
        commitmentStatus: specifyStatus(fund.monitor_data.Commitment),
        valueAtRiskStatus: specifyStatus(fund.monitor_data.Value_at_Risk),
        commitmentValue:
          fundType === 'commitment' || fundType === 'both'
            ? fund.monitor_data.commitment_value
            : -1,
        valueAtRiskValue:
          fundType === 'var' || fundType === 'both'
            ? fund.monitor_data.var_value
            : -1,
        varDay: fund.monitor_data.var_day,
        varFund: fund.monitor_data.var_fund,
        ucitsLaw: specifyStatus(fund.monitor_data.UCITS),
        riaifLaw: specifyStatus(fund.monitor_data.RIAIF),
        qiaifLaw: specifyStatus(fund.monitor_data.QIAIF),
        act40Law: specifyStatus(fund.monitor_data.ACT40),
        nursLaw: specifyStatus(fund.monitor_data.NURS),
        fivgLaw: specifyStatus(fund.monitor_data.FIVG),
        partiiLaw: specifyStatus(fund.monitor_data.PARTII),
        supplementaryRules: specifyStatus(
          fund.monitor_data?.Supplementary_Rules,
        ),
        prospectusStatus: specifyStatus(
          fund.monitor_data.Risk_Compliance ??
            fund.monitor_data.risk_compliance_status,
        ),
        countryExposure: fund.monitor_data.country_exposure,
        positionExposure: fund.monitor_data.position_exposure,
        derogationMessage:
          fund.monitor_data.six_month_derogation?.result === 'live'
            ? `Derogation Expiry Date: ${fund.monitor_data.six_month_derogation.derogation_date}`
            : undefined,
        timeSeriesData: null,
        timeSeriesStatus: null,
        notes: true,
        leverage: specifyStatus(fund.monitor_data.Leverage),
        fundType: fundType,
        cssfRiskSpreading: specifyStatus(fund.monitor_data['Risk Spreading']),
        totalCommitmentPercent: fund.monitor_data.total_commitment_pct,
        fundVar: fund.monitor_data.fund_var,
        totalGrossExposurePercent: fund.monitor_data.total_gross_exposure_pct,
        market: specifyStatus(fund.monitor_data.Market),
        counterparty: specifyStatus(fund.monitor_data.Counterparty),
        credit: specifyStatus(fund.monitor_data.Credit),
        liquidity: specifyStatus(fund.monitor_data.Liquidity),
        ...fund.monitor_data,
        isDemoFund: Boolean(pageFund?.is_demo_fund),
      } as GeneralOverviewTableData;

      if (fund.monitor_data.historical_time_series) {
        const timeSeries = fund.monitor_data.historical_time_series;
        timeSeries.shift();
        toAdd['timeSeriesData'] = timeSeries;
      }

      if (fund.monitor_data.historical_time_series_status)
        toAdd['timeSeriesStatus'] =
          fund.monitor_data.historical_time_series_status;

      finalData.push(toAdd);
    });
  }
  return finalData;
}

type OverviewPageData = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
  subFundRiskReports: ReportsListByFund;
};

type OverviewTableSectionProps = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
  subFundRiskReports: ReportsListByFund;
  clientName: string | null;
  section: string;
  sectionId?: string;
  sectionTitle: string;
  positionDate?: string;
  getNotesForFund: (fundName: string, positionDate: string) => Note[];
  preFetchedNotesLoading: boolean;
};

const OverviewTableSection: React.FC<OverviewTableSectionProps> = ({
  overview,
  reports,
  subFundRiskReports,
  clientName,
  section,
  sectionId,
  sectionTitle,
  positionDate,
  getNotesForFund,
  preFetchedNotesLoading,
}) => {
  const columnsFields = useMemo(
    () =>
      addSupplementaryRulesColumn(
        getOverviewColumnsFields(clientName, sectionId),
        overview.data,
      ),
    [clientName, sectionId, overview.data],
  );

  const dataPreparedForTable = useMemo(
    () =>
      createGeneralTableData(
        overview.data,
        overview.funds ?? [],
        reports,
        subFundRiskReports,
        clientName,
      ),
    [overview.data, overview.funds, reports, subFundRiskReports, clientName],
  );

  const columns = useMemo(
    () =>
      columnsFields
        ? getOverviewColumnsV2(
          clientName,
          columnsFields,
          section,
          getNotesForFund,
          preFetchedNotesLoading,
        )
        : [],
    [
      clientName,
      columnsFields,
      section,
      getNotesForFund,
      preFetchedNotesLoading,
    ],
  );

  const exportFields = useMemo(
    () =>
      (columnsFields ?? []).filter(
        (field) => field !== 'reports' && field !== 'index',
      ),
    [columnsFields],
  );

  const supportsDetailPanel = (sectionId ?? section) !== 'aifmd';

  const renderDetail = useCallback(
    (row: GeneralOverviewTableData) => (
      <OverviewSecondLevelGraphs
        fundName={row.internalFundName}
        positionDate={row.positionDate}
        varFund={row.varFund === 'yes'}
        passValue={mapStatusToColor(specifyStatus(row.timeSeriesStatus))}
      />
    ),
    [],
  );

  const getRowId = useCallback(
    (row: GeneralOverviewTableData) => row.internalFundName,
    [],
  );

  if (!columnsFields) {
    return (
      <NoDataMessage
        message={`No configuration found for the section ${sectionId ?? section}`}
      />
    );
  }

  return (
    <OverviewTableV2<GeneralOverviewTableData>
      data={dataPreparedForTable}
      columns={columns}
      title={`${sectionTitle} Overview Data`}
      pdfIdentifier={`${section}-overview`}
      positionDate={positionDate}
      exportFields={exportFields}
      initialColumnVisibility={overviewInitialColumnVisibility}
      emptyMessage="No funds found"
      noMatchesMessage="No matches for current search"
      getRowId={getRowId}
      renderDetail={supportsDetailPanel ? renderDetail : undefined}
      getIsDemoRow={(row) => row.isDemoFund}
    />
  );
};

/**
 * TanStack + virtualised overview page.
 */
const OverviewPage: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props,
) => {
  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;
  const clientName = useSelector(clientNameSelector);
  const selectedRaptorPlatformId = useSelector(raptorPlatformIdSelector);
  const includeSubFundRiskReports = clientName?.includes('pure') ?? false;

  const { reportsQuery, overviewQuery, subFundRiskReportsQuery } =
    useOverviewQueries({
      fundTypes,
      positionDate: props.positionDate,
      raptorPlatformId: selectedRaptorPlatformId,
      includeSubFundRiskReports,
    });

  const reportsReady =
    !reportsQuery.isLoading &&
    (!includeSubFundRiskReports || !subFundRiskReportsQuery.isLoading);

  const pageData = useMemo((): OverviewPageData | null => {
    if (!overviewQuery.data?.data || !reportsReady) {
      return null;
    }

    const overviewRows = [...overviewQuery.data.data].sort((a, b) =>
      sortFundNames(a.fund_name || '', b.fund_name || ''),
    );

    return {
      overview: {
        ...overviewQuery.data,
        data: overviewRows,
      },
      reports: reportsQuery.data ?? {},
      subFundRiskReports: subFundRiskReportsQuery.data ?? {},
    };
  }, [
    overviewQuery.data,
    reportsQuery.data,
    subFundRiskReportsQuery.data,
    reportsReady,
  ]);

  const overviewRows = overviewQuery.data?.data ?? [];

  const fundNames = overviewRows.map((fund) => fund.fund_name);

  const positionDates = overviewRows
    .map((fund) => fund.monitor_data?.selected_position_date)
    .filter((d): d is string => !!d);

  const sortedDates = [...positionDates].sort();
  const earliestDate = sortedDates[0];
  const latestDate = sortedDates[sortedDates.length - 1];

  const { fundNotesData, loading: preFetchedNotesLoading } = useAllFundNotes({
    fundNames,
    date1: earliestDate || '',
    date2: latestDate,
    subject: undefined,
    topic: undefined,
    shouldCall: !!earliestDate && fundNames.length > 0,
  });

  const notesByFundAndDate = useMemo(() => {
    const map = new Map<string, Note[]>();
    Object.entries(fundNotesData).forEach(([fundName, notes]) => {
      notes.forEach((note: Note) => {
        const key = `${fundName}-${note.fund_note_position_date}`;
        const existing = map.get(key) || [];
        existing.push(note);
        map.set(key, existing);
      });
    });
    return map;
  }, [fundNotesData]);

  const getNotesForFund = useCallback(
    (fundName: string, positionDate: string): Note[] => {
      const key = `${fundName}-${positionDate}`;
      return notesByFundAndDate.get(key) || [];
    },
    [notesByFundAndDate],
  );

  const isLoading =
    overviewQuery.isLoading ||
    reportsQuery.isLoading ||
    (includeSubFundRiskReports && subFundRiskReportsQuery.isLoading);

  const isFetching =
    overviewQuery.isFetching ||
    reportsQuery.isFetching ||
    (includeSubFundRiskReports && subFundRiskReportsQuery.isFetching);

  return (
    <QueryBoundary<OverviewPageData>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={!!overviewQuery.error}
      error={overviewQuery.error}
      data={pageData}
      loadingMessages={['Loading Overview Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {({ overview, reports, subFundRiskReports }) => (
        <OverviewTableSection
          overview={overview}
          reports={reports}
          subFundRiskReports={subFundRiskReports}
          clientName={clientName}
          section={props.section}
          sectionId={sectionDetails?.sectionId}
          sectionTitle={sectionDetails?.sectionTitle?.toUpperCase() ?? ''}
          positionDate={props.positionDate}
          getNotesForFund={getNotesForFund}
          preFetchedNotesLoading={preFetchedNotesLoading}
        />
      )}
    </QueryBoundary>
  );
};

export default OverviewPage;
